import {useState, useEffect} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {useForm, Controller} from 'react-hook-form';
import {Password} from 'primereact/password';
import {Message} from 'primereact/message';
import Button from '@/core/components/button/Button';
import {apiService} from '@/core/api';
import {API_ENDPOINTS} from '@/core/constants/endpoints';
import {ROUTES} from '@/core/constants/app';
import {imageList} from '@/core/utils/imageList';
import './PasswordReset.scss';

interface PasswordResetFormData {
  password: string;
  confirmPassword: string;
}

export const PasswordReset = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    formState: {errors},
  } = useForm<PasswordResetFormData>({
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const password = watch('password');

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token. Please request a new password reset.');
    }
  }, [token]);

  const onSubmit = async (data: PasswordResetFormData) => {
    // console.log('Form submitted with data:', data);
    // console.log('Token:', token);

    if (!token) {
      setError('Invalid reset token');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // console.log('Sending request to:', API_ENDPOINTS.SIGNUP.PASSWORD_RESET);
      // console.log('Request payload:', {token, password: data.password});

      const response: any = await apiService.post<{status: string}>(API_ENDPOINTS.SIGNUP.PASSWORD_RESET, {
        token,
        password: data.password,
      });

      // console.log('Response received:', response);

      if (response?.success == true) {
        setSuccess(true);
      } else {
        setError('Password reset failed. Please try again or request a new reset link.');
      }
    } catch (err: any) {
      console.error('Password reset error:', err);
      setError(
        err.response?.data?.message || 'Failed to reset password. The link may have expired. Please request a new one.',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="password-reset-page">
      <div className="password-reset-container">
        <div className="password-reset-header">
          <img src={imageList.logo} alt="EU Calls" className="logo" />
          <h1>Set New Password</h1>
          <p>Please enter your new password below</p>
        </div>

        {success ? (
          <div className="success-container">
            <Message severity="success" text="Your password has been reset successfully!" className="w-full mb-3" />
            <p className="text-center mb-4">You can now log in with your new password.</p>
            <Button variant="primary" size="large" onClick={() => navigate(ROUTES.LOGIN)} className="w-full">
              Go to Login Page
            </Button>
          </div>
        ) : (
          <form className="password-reset-form" onSubmit={handleSubmit(onSubmit)}>
            {error && <Message severity="error" text={error} className="w-full mb-3" />}

            <div className="form-group">
              <label htmlFor="password">New Password</label>
              <Controller
                name="password"
                control={control}
                rules={{
                  required: 'Password is required',
                  minLength: {
                    value: 3,
                    message: 'Password must be at least 8 characters long',
                  },
                }}
                render={({field}) => (
                  <Password
                    {...field}
                    placeholder="Enter new password"
                    className={`password-input ${errors.password ? 'p-invalid' : ''}`}
                    toggleMask
                    feedback={false}
                    style={{width: '100%'}}
                  />
                )}
              />
              {errors.password && <small className="p-error">{errors.password.message}</small>}
              <small style={{color: 'grey'}}>Must be at least 8 characters</small>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <Controller
                name="confirmPassword"
                control={control}
                rules={{
                  required: 'Please confirm your password',
                  validate: (value) =>
                    value === password || 'The passwords you entered do not match. Please try again.',
                }}
                render={({field}) => (
                  <Password
                    {...field}
                    placeholder="Confirm new password"
                    className={`password-input ${errors.confirmPassword ? 'p-invalid' : ''}`}
                    toggleMask
                    feedback={false}
                    style={{width: '100%'}}
                  />
                )}
              />
              {errors.confirmPassword && <small className="p-error">{errors.confirmPassword.message}</small>}
            </div>

            <Button
              type="submit"
              className="reset-btn w-full"
              variant="primary"
              size="large"
              loading={loading}
              disabled={loading || !token}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>

            <div className="form-footer">
              <p>
                Remember your password?{' '}
                <a
                  href="#"
                  className="link"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(ROUTES.LOGIN);
                  }}
                >
                  Back to Login
                </a>
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
